import { Link } from 'react-router-dom';

const VALUES = [
  {
    title: 'Customer-First Engineering',
    description:
      'Every product decision starts with the end user. We design for real-world operational demands, not boardroom demonstrations.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      </svg>
    ),
  },
  {
    title: 'Operational Security',
    description:
      'Your data stays on your hardware. Zero-trust architectures and secure on-premise deployment keep sensitive data under your control at all times.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
    ),
  },
  {
    title: 'Continuous Innovation',
    description:
      'We invest heavily in R&D to stay ahead of evolving challenges. Our AI models are retrained and improved with every operational cycle.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
  },
  {
    title: 'Trusted Partnerships',
    description:
      'We work alongside government agencies and enterprise clients as long-term strategic partners -- not just vendors.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
];

export default function About() {
  return (
    <div className="relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 left-1/3 w-[500px] h-[500px] rounded-full bg-cyan-500/8 blur-3xl" />
        <div className="absolute bottom-1/4 -right-40 w-[400px] h-[400px] rounded-full bg-blue-500/8 blur-3xl" />
      </div>

      {/* ==================== HERO ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-sm text-cyan-400 font-medium">About Us</span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            Secure AI for{' '}
            <span className="text-gradient">Government & Enterprise</span>
          </h1>

          <p className="mt-6 text-lg text-slate-400 leading-relaxed">
            Tritech Data Pte Ltd delivers secure, AI-powered solutions for government and
            enterprise clients. We specialize in on-premise AI appliances that provide real-time
            analytics, secure data processing, and intelligent decision support &mdash; designed
            for organizations that require secure on-premise AI and operational reliability.
          </p>
        </div>
      </section>

      {/* ==================== COMPANY OVERVIEW ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Mission */}
          <div className="bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 sm:p-10">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 mb-6">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
            <p className="text-slate-400 leading-relaxed">
              To provide advanced AI solutions that enhance operational efficiency and protect
              critical infrastructure. We believe that cutting-edge artificial intelligence, deployed
              on-premise at the edge, is the cornerstone of modern enterprise operations -- and we are
              committed to making it accessible, reliable, and operationally effective for organizations worldwide.
            </p>
          </div>

          {/* Vision */}
          <div className="bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 sm:p-10">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 mb-6">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
            <p className="text-slate-400 leading-relaxed">
              To be the leading provider of edge-AI solutions in the Asia-Pacific region
              and beyond. We envision a world where organizations have instant access to
              intelligent, actionable insights -- enabling faster decisions, safer operations, and
              stronger capabilities against evolving challenges.
            </p>
          </div>
        </div>
      </section>

      {/* ==================== VALUES ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Our <span className="text-gradient">Values</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            The principles that guide everything we build, deploy, and support.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {VALUES.map((v) => (
            <div
              key={v.title}
              className="group relative bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 mb-5">
                  {v.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{v.title}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">{v.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== LOCATION ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-28">
        <div className="bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 sm:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">
                Headquartered in <span className="text-gradient">Singapore</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                Strategically located in Singapore -- a global hub for technology and
                innovation -- we serve clients across the Asia-Pacific, Middle East, and
                beyond.
              </p>
              <div className="space-y-3 text-slate-300">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  <div>
                    <p>61 Kaki Bukit Avenue 1</p>
                    <p>#04-44, Shun Li Industrial Park</p>
                    <p>Singapore 417943</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-cyan-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  <a href="mailto:info@tritechdatasg.com" className="text-cyan-400 hover:underline">
                    info@tritechdatasg.com
                  </a>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to="/contact"
                className="px-10 py-3 btn-gradient text-white font-semibold rounded-lg"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
